import type { Dispatch, SetStateAction } from 'react';
import type { Device } from '@eon/types';
import { useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import { Button, Card, CardContent, CardHeader, CardTitle, Field, NumberField, SelectField, TextField } from '@eon/ui';
import { ConnectorSelect } from '@/components/ConnectorSelect';
import { IdTagPicker } from '@/components/IdTagPicker';
import { COMMANDS } from './commands';
import type { ActionRunner, CommandId } from './types';

const TRIGGER_OPTIONS = [
  { value: 'BootNotification', labelKey: 'deviceControl.triggerMessages.BootNotification' },
  { value: 'Heartbeat', labelKey: 'deviceControl.triggerMessages.Heartbeat' },
  { value: 'MeterValues', labelKey: 'deviceControl.triggerMessages.MeterValues' },
  { value: 'StatusNotification', labelKey: 'deviceControl.triggerMessages.StatusNotification' },
  { value: 'FirmwareStatusNotification', labelKey: 'deviceControl.triggerMessages.FirmwareStatusNotification' },
  { value: 'DiagnosticsStatusNotification', labelKey: 'deviceControl.triggerMessages.DiagnosticsStatusNotification' },
] as const;
type TriggerKind = (typeof TRIGGER_OPTIONS)[number]['value'];

const RESET_OPTIONS = [
  { value: 'Soft', labelKey: 'deviceControl.commands.reset.soft' },
  { value: 'Hard', labelKey: 'deviceControl.commands.reset.hard' },
] as const;

const AVAILABILITY_OPTIONS = [
  { value: 'Operative', labelKey: 'deviceControl.commands.changeAvailability.operative' },
  { value: 'Inoperative', labelKey: 'deviceControl.commands.changeAvailability.inoperative' },
] as const;

export interface CommandFormState {
  rsConnector: number | '';
  rsTag: string;
  rsTx: number | '';
  rsStopConnector: number | '';
  chConnector: number | '';
  chType: 'Operative' | 'Inoperative';
  unlockConnector: number | '';
  resetType: 'Soft' | 'Hard';
  trgMessage: TriggerKind;
  trgConnector: number | '';
  getCfgKeys: string;
  changeCfgKey: string;
  changeCfgValue: string;
  cpConnector: number | '';
  cpLimit: number | '';
  cpClearId: number | '';
}

export interface CommandFormPanelProps {
  commandId: CommandId;
  device: Device;
  deviceId: string;
  offline: boolean;
  pending: string | null;
  state: CommandFormState;
  setState: Dispatch<SetStateAction<CommandFormState>>;
  run: (runner: ActionRunner) => Promise<void>;
}

function numOrUndef(v: number | '') {
  return v === '' ? undefined : v;
}

export function CommandFormPanel({
  commandId,
  device,
  deviceId,
  offline,
  pending,
  state,
  setState,
  run,
}: CommandFormPanelProps) {
  const { t } = useTranslation();
  const def = COMMANDS.find((c) => c.id === commandId)!;
  const connectors = device.connectors ?? [];
  const busy = pending !== null;
  const isPending = pending === def.id;

  const remoteStart = hooks.useRemoteStart();
  const remoteStop = hooks.useRemoteStop();
  const reset = hooks.useResetDevice();
  const change = hooks.useChangeAvailability();
  const unlock = hooks.useUnlockConnector();
  const clearCache = hooks.useClearCache();
  const trigger = hooks.useTriggerMessage();
  const getCfg = hooks.useGetConfiguration();
  const changeCfg = hooks.useChangeConfiguration();
  const clearCp = hooks.useClearChargingProfile();
  const setCp = hooks.useSetChargingProfile();

  const patch = (p: Partial<CommandFormState>) => setState((s) => ({ ...s, ...p }));

  const submit = () => {
    const actionLabelKey = def.labelKey;
    switch (commandId) {
      case 'remote-start':
        return run({
          action: 'RemoteStartTransaction',
          actionLabelKey,
          request: { connectorId: numOrUndef(state.rsConnector), idTag: state.rsTag },
          fn: () =>
            remoteStart.mutateAsync({
              deviceId,
              connectorId: state.rsConnector === '' ? 1 : state.rsConnector,
              idTag: state.rsTag,
            }),
        });
      case 'remote-stop':
        return run({
          action: 'RemoteStopTransaction',
          actionLabelKey,
          request:
            state.rsTx === ''
              ? { connectorId: numOrUndef(state.rsStopConnector) }
              : { transactionId: state.rsTx },
          fn: () =>
            remoteStop.mutateAsync({
              deviceId,
              transactionId: state.rsTx === '' ? undefined : state.rsTx,
              connectorId: state.rsTx === '' ? numOrUndef(state.rsStopConnector) : undefined,
            }),
        });
      case 'unlock-connector':
        return run({
          action: 'UnlockConnector',
          actionLabelKey,
          request: { connectorId: state.unlockConnector },
          fn: () =>
            unlock.mutateAsync({
              deviceId,
              connectorId: state.unlockConnector === '' ? 1 : state.unlockConnector,
            }),
        });
      case 'change-availability':
        return run({
          action: 'ChangeAvailability',
          actionLabelKey,
          request: { connectorId: state.chConnector, type: state.chType },
          fn: () =>
            change.mutateAsync({
              deviceId,
              connectorId: state.chConnector === '' ? 0 : state.chConnector,
              type: state.chType,
            }),
        });
      case 'reset':
        return run({
          action: 'Reset',
          actionLabelKey,
          request: { type: state.resetType },
          fn: () => reset.mutateAsync({ deviceId, type: state.resetType }),
        });
      case 'clear-cache':
        return run({
          action: 'ClearCache',
          actionLabelKey,
          request: {},
          fn: () => clearCache.mutateAsync({ deviceId }),
        });
      case 'trigger-message':
        return run({
          action: 'TriggerMessage',
          actionLabelKey,
          request: {
            requestedMessage: state.trgMessage,
            connectorId: numOrUndef(state.trgConnector),
          },
          fn: () =>
            trigger.mutateAsync({
              deviceId,
              requestedMessage: state.trgMessage,
              connectorId: numOrUndef(state.trgConnector),
            }),
        });
      case 'get-configuration': {
        const keys = state.getCfgKeys
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        return run({
          action: 'GetConfiguration',
          actionLabelKey,
          request: { key: keys },
          fn: () => getCfg.mutateAsync({ deviceId, keys }),
        });
      }
      case 'change-configuration':
        return run({
          action: 'ChangeConfiguration',
          actionLabelKey,
          request: { key: state.changeCfgKey, value: state.changeCfgValue },
          fn: () =>
            changeCfg.mutateAsync({
              deviceId,
              key: state.changeCfgKey,
              value: state.changeCfgValue,
            }),
        });
      case 'set-charging-profile': {
        const limit = state.cpLimit === '' ? 16 : state.cpLimit;
        const profile = {
          chargingProfileId: Math.floor(Date.now() / 1000) % 100000,
          stackLevel: 0,
          chargingProfilePurpose: 'TxDefaultProfile',
          chargingProfileKind: 'Absolute',
          chargingSchedule: {
            chargingRateUnit: 'A',
            chargingSchedulePeriod: [{ startPeriod: 0, limit }],
          },
        };
        return run({
          action: 'SetChargingProfile',
          actionLabelKey,
          request: { connectorId: state.cpConnector, csChargingProfiles: profile },
          fn: () =>
            setCp.mutateAsync({
              deviceId,
              connectorId: state.cpConnector === '' ? 1 : state.cpConnector,
              csChargingProfiles: profile,
            }),
        });
      }
      case 'clear-charging-profile':
        return run({
          action: 'ClearChargingProfile',
          actionLabelKey,
          request: state.cpClearId === '' ? {} : { id: state.cpClearId },
          fn: () =>
            clearCp.mutateAsync({
              deviceId,
              id: state.cpClearId === '' ? undefined : state.cpClearId,
            }),
        });
      default:
        return Promise.resolve();
    }
  };

  const canSubmit = (() => {
    if (offline || busy) return false;
    switch (commandId) {
      case 'remote-start':
        return Boolean(state.rsTag);
      case 'remote-stop':
        return true; // transactionId optional — backend resolves the active session
      case 'change-configuration':
        return Boolean(state.changeCfgKey);
      default:
        return true;
    }
  })();

  return (
    <Card className={offline ? 'opacity-60' : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="bg-muted flex h-9 w-9 items-center justify-center rounded-lg">{def.icon}</span>
          {t(def.labelKey)}
        </CardTitle>
        <p className="text-muted-foreground text-sm">{t(def.descriptionKey)}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {offline ? (
          <p className="text-destructive text-sm">{t('errors.device.offline')}</p>
        ) : null}

        {commandId === 'remote-start' ? (
          <>
            <Field label={t('deviceControl.fields.connector')} hint={t('deviceControl.fields.connectorOptional')}>
              <ConnectorSelect
                connectors={connectors}
                value={state.rsConnector}
                onChange={(v) => patch({ rsConnector: v })}
                disabled={offline}
              />
            </Field>
            <Field label={t('deviceControl.fields.idTag')} hint={t('deviceControl.fields.idTagHint')} required>
              <IdTagPicker value={state.rsTag} onValueChange={(v) => patch({ rsTag: v })} disabled={offline} />
            </Field>
          </>
        ) : null}

        {commandId === 'remote-stop' ? (
          <>
            <Field label={t('deviceControl.fields.connector')} hint={t('deviceControl.fields.stopConnectorHint')}>
              <ConnectorSelect
                connectors={connectors}
                value={state.rsStopConnector}
                onChange={(v) => patch({ rsStopConnector: v })}
                disabled={offline}
              />
            </Field>
            <Field label={t('deviceControl.fields.transactionId')} hint={t('deviceControl.fields.transactionIdHint')}>
              <NumberField value={state.rsTx} onChange={(v) => patch({ rsTx: v })} min={1} disabled={offline} />
            </Field>
          </>
        ) : null}

        {commandId === 'unlock-connector' ? (
          <Field label={t('deviceControl.fields.connector')}>
            <ConnectorSelect
              connectors={connectors}
              value={state.unlockConnector}
              onChange={(v) => patch({ unlockConnector: v })}
              disabled={offline}
            />
          </Field>
        ) : null}

        {commandId === 'change-availability' ? (
          <>
            <Field label={t('deviceControl.fields.connector')} hint={t('deviceControl.fields.connectorZero')}>
              <ConnectorSelect
                connectors={connectors}
                value={state.chConnector}
                onChange={(v) => patch({ chConnector: v })}
                disabled={offline}
                allowZero
              />
            </Field>
            <Field label={t('deviceControl.fields.availabilityType')}>
              <SelectField<'Operative' | 'Inoperative'>
                value={state.chType}
                onValueChange={(v) => patch({ chType: v })}
                disabled={offline}
                options={AVAILABILITY_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey),
                }))}
              />
            </Field>
          </>
        ) : null}

        {commandId === 'reset' ? (
          <Field label={t('deviceControl.fields.resetType')}>
            <SelectField<'Soft' | 'Hard'>
              value={state.resetType}
              onValueChange={(v) => patch({ resetType: v })}
              disabled={offline}
              options={RESET_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
                description: o.value === 'Soft' ? t('deviceControl.commands.reset.softDesc') : t('deviceControl.commands.reset.hardDesc'),
              }))}
            />
          </Field>
        ) : null}

        {commandId === 'clear-cache' ? (
          <p className="text-muted-foreground text-sm">{t('deviceControl.commands.clearCache.hint')}</p>
        ) : null}

        {commandId === 'trigger-message' ? (
          <>
            <Field label={t('deviceControl.fields.message')}>
              <SelectField<TriggerKind>
                value={state.trgMessage}
                onValueChange={(v) => patch({ trgMessage: v })}
                disabled={offline}
                options={TRIGGER_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.labelKey),
                }))}
              />
            </Field>
            <Field label={t('deviceControl.fields.connector')} hint={t('deviceControl.fields.connectorDeviceLevel')}>
              <NumberField value={state.trgConnector} onChange={(v) => patch({ trgConnector: v })} min={0} disabled={offline} />
            </Field>
          </>
        ) : null}

        {commandId === 'get-configuration' ? (
          <Field label={t('deviceControl.fields.configKeys')} hint={t('deviceControl.fields.configKeysHint')}>
            <TextField
              value={state.getCfgKeys}
              onChange={(e) => patch({ getCfgKeys: e.target.value })}
              placeholder={t('deviceControl.fields.configKeysPlaceholder')}
              disabled={offline}
            />
          </Field>
        ) : null}

        {commandId === 'change-configuration' ? (
          <>
            <Field label={t('deviceControl.fields.configKey')}>
              <TextField
                value={state.changeCfgKey}
                onChange={(e) => patch({ changeCfgKey: e.target.value })}
                placeholder={t('deviceControl.fields.configKeyPlaceholder')}
                disabled={offline}
              />
            </Field>
            <Field label={t('deviceControl.fields.configValue')}>
              <TextField
                value={state.changeCfgValue}
                onChange={(e) => patch({ changeCfgValue: e.target.value })}
                placeholder={t('deviceControl.fields.configValuePlaceholder')}
                disabled={offline}
              />
            </Field>
          </>
        ) : null}

        {commandId === 'set-charging-profile' ? (
          <>
            <Field label={t('deviceControl.fields.connector')}>
              <ConnectorSelect
                connectors={connectors}
                value={state.cpConnector}
                onChange={(v) => patch({ cpConnector: v })}
                disabled={offline}
              />
            </Field>
            <Field label={t('deviceControl.fields.currentLimit')} hint={t('deviceControl.fields.currentLimitHint')}>
              <NumberField value={state.cpLimit} onChange={(v) => patch({ cpLimit: v })} min={6} max={80} disabled={offline} />
            </Field>
          </>
        ) : null}

        {commandId === 'clear-charging-profile' ? (
          <Field label={t('deviceControl.fields.profileId')} hint={t('deviceControl.fields.profileIdHint')}>
            <NumberField value={state.cpClearId} onChange={(v) => patch({ cpClearId: v })} min={1} disabled={offline} />
          </Field>
        ) : null}

        <Button size="default" disabled={!canSubmit || isPending} onClick={() => void submit()}>
          {isPending ? t('deviceControl.sending') : t('deviceControl.send')}
        </Button>
      </CardContent>
    </Card>
  );
}
